import React, { useState, useMemo } from 'react';
import { MicroSession } from '@/data/microSessions';
import { Shield, Search, Puzzle, MessageSquareDashed, Play, Handshake, PenTool, Siren } from 'lucide-react';

interface MicroSessionCardProps {
  session: MicroSession;
  onStart?: (session: MicroSession) => void;
  status?: 'not_started' | 'in_progress' | 'completed';
  overline?: string;
  recommended?: boolean;
  userId?: string;
}

const skillTokens: Record<
  MicroSession['skillArea'],
  { pillBg: string; pillText: string; dot: string; border: string; ring: string }
> = {
  Foundations: { pillBg: 'bg-sky-50', pillText: 'text-sky-700', dot: 'bg-sky-400', border: 'border-sky-100', ring: 'stroke-sky-400' },
  Communication: { pillBg: 'bg-emerald-50', pillText: 'text-emerald-700', dot: 'bg-emerald-400', border: 'border-emerald-100', ring: 'stroke-emerald-400' },
  Operations: { pillBg: 'bg-amber-50', pillText: 'text-amber-700', dot: 'bg-amber-400', border: 'border-amber-100', ring: 'stroke-amber-400' },
  Automation: { pillBg: 'bg-fuchsia-50', pillText: 'text-fuchsia-700', dot: 'bg-fuchsia-400', border: 'border-fuchsia-100', ring: 'stroke-fuchsia-400' },
  Leadership: { pillBg: 'bg-purple-50', pillText: 'text-purple-700', dot: 'bg-purple-400', border: 'border-purple-100', ring: 'stroke-purple-400' },
  Insight: { pillBg: 'bg-rose-50', pillText: 'text-rose-700', dot: 'bg-rose-400', border: 'border-rose-100', ring: 'stroke-rose-400' }
};

const modeConfig: Record<string, { label: string; icon: any; color: string }> = {
  detective: {
    label: 'Audit',
    icon: Search,
    color: 'text-violet-600'
  },
  puzzle: {
    label: 'Discover',
    icon: Puzzle,
    color: 'text-amber-600'
  },
  simulation: {
    label: 'Practice',
    icon: MessageSquareDashed,
    color: 'text-rose-600'
  },
  negotiation: {
    label: 'Advocacy',
    icon: Handshake,
    color: 'text-indigo-600'
  },
  curation: {
    label: 'Refine',
    icon: PenTool,
    color: 'text-emerald-600'
  },
  triage: {
    label: 'Decision',
    icon: Siren,
    color: 'text-rose-600'
  }
};

export const MicroSessionCard: React.FC<MicroSessionCardProps> = ({
  session,
  onStart,
  status = 'not_started',
  overline,
  recommended,
  userId
}) => {
  const badgeTexts = useMemo(
    () => [
      'Finished',
      'Wrapped',
      'Logged',
      'Saved',
      'Locked In',
      'Secured',
      'Achieved',
      'Captured',
      'Recorded',
      'All Set',
      'Marked Done',
      'Marked Complete',
      'Checked Off',
      'Task Closed',
      'Done Today',
      'Progress Saved',
      'Finalized',
      'Confirmed',
      'Ship It',
      'Signed Off',
      'Verified',
      'Sealed',
      'Settled',
      'Archived',
      'Buttoned Up',
      'Locked',
      'Filed',
      'Stowed',
      'Completed Work',
      'Marked Safe',
      'Goal Met',
      'Noted',
      'Pinned',
      'Reconciled'
    ],
    []
  );

  const pickBadgeText = useMemo(() => {
    return (id: string, userSeed?: string) => {
      // Deterministic pseudo-random: depends on lesson id and user id if available
      const seed = `${id}-${userSeed || 'anon'}`;
      let hash = 0;
      for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
      }
      return badgeTexts[hash % badgeTexts.length];
    };
  }, [badgeTexts]);

  const [showDetails, setShowDetails] = useState(true); // Default to visible for touch users
  const tokens = skillTokens[session.skillArea] || {
    pillBg: 'bg-slate-100',
    pillText: 'text-slate-700',
    dot: 'bg-slate-400',
    border: 'border-slate-200',
    ring: 'stroke-slate-400'
  };

  // Ring calculation
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const progressOffset = status === 'completed' ? 0 : status === 'in_progress' ? circumference * 0.5 : circumference;

  const mode = session.learningMode && modeConfig[session.learningMode] ? modeConfig[session.learningMode] : null;

  return (
    <button
      onClick={() => onStart?.(session)}
      onMouseEnter={() => setShowDetails(true)}
      onMouseLeave={() => setShowDetails(false)}
      onFocus={() => setShowDetails(true)}
      onBlur={() => setShowDetails(false)}
      className={`group relative flex flex-col gap-3 rounded-3xl border ${recommended ? 'border-slate-900 ring-1 ring-slate-900' : status === 'completed' ? 'border-emerald-100' : tokens.border
        } ${status === 'completed' ? 'bg-emerald-50' : 'bg-white'} p-5 text-left text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400`}
      aria-label={`${session.title} • ${session.skillArea} • ${status.replace('_', ' ')}`}
      aria-expanded={showDetails}
    >
      {/* Recommended Badge */}
      {recommended && status !== 'completed' && (
        <div className="absolute -top-2 -right-2 bg-slate-900 text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider shadow-sm z-10">
          Start here
        </div>
      )}

      {/* Progress ring (shows until completion) or completion badge */}
      {status === 'completed' ? (
        <div className="absolute top-4 right-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white shadow border border-emerald-100">
            <Shield className="w-4 h-4 text-emerald-500" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-700">
              {pickBadgeText(session.id, userId)}
            </span>
          </div>
        </div>
      ) : (
        <div className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
            <circle
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              className="stroke-slate-100"
              strokeWidth="3"
            />
            <circle
              cx="18"
              cy="18"
              r={radius}
              fill="none"
              className={`${tokens.ring} transition-all duration-500 ease-out`}
              strokeWidth="3"
              strokeDasharray={circumference}
              strokeDashoffset={progressOffset}
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}

      <div className="flex items-center justify-between pr-8">
        {overline ? (
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-medium">
            {overline}
          </span>
        ) : (
          <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.3em] ${tokens.pillBg} ${tokens.pillText}`}
          >
            <span className={`h-2 w-2 rounded-full ${tokens.dot}`} />
            {session.skillArea}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mt-1">
        <div className="pr-8">
          <h3 className="text-lg font-semibold text-slate-900">{session.title}</h3>
          {session.displayTitle && (
            <p className="text-sm text-slate-700 font-medium mt-0.5">
              {session.displayTitle}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-auto">
        {mode ? (
          <span className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-medium ${mode.color}`}>
            <mode.icon className="w-3.5 h-3.5" />
            Tap to {mode.label}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-slate-400 group-hover:text-slate-600 transition-colors">
            <Play className="w-3 h-3 fill-current opacity-50" />
            Tap to start
          </span>
        )}
      </div>

    </button>
  );
};
